import React, { useEffect, useRef } from 'react';
import {
  FlatList,
  Image,
  Platform,
  StyleProp,
  StyleSheet,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { useMediaQuery } from 'react-responsive';

import useCssScrollSnap from '../../hooks/use-css-scroll-snap';
import generateID from '../../utils/generate-id';

type Item = {
  id: string;
  imageUri: string;
};

type CarouselItemProp = Item & {
  accessibilityLabel?: string;
  onPress?: (item: CarouselItemProp) => void;
  wrapItem?: boolean;
  /**
   * Automatically generated based on Carousel ID prop
   */
  itemId?: string;
};

type Props = {
  items: Item[];
  onItemPress?: CarouselItemProp['onPress'];
  /**
   * Disables scrolling and forces items to wrap around
   */
  wrapContent?: boolean;
  /**
   * Number of items per row.
   * This disables horizontal scrolling.
   */
  numColumns?: number;
  /**
   * Customize the Carousel container's appearance. Useful for some responsive layout.
   */
  style?: StyleProp<ViewStyle>;
  /**
   * Customize the scrolling viewport's styles
   */
  viewPortStyle?: StyleProp<ViewStyle>;
  /**
   * Accessibility label for Carousel
   */
  accessibilityLabel?: string;
  /**
   * Accessibility label for Carousel Item
   */
  itemAccessibilityLabel?: CarouselItemProp['accessibilityLabel'];
  id?: string;
};

const Carousel = (props: Props) => {
  useCssScrollSnap();
  const flatListRef = useRef<FlatList>(null);

  const numColumns = props.numColumns || 0;

  /**
   * Only for Web
   * Sets custom CSS rules to enable scroll snapping
   */
  useEffect(() => {
    if (Platform.OS === 'web') {
      flatListRef.current?.setNativeProps({
        className: 'rnw-scroll-snap-container',
      });
    }
  }, []);

  const isLastItem = (index: number) => {
    if (numColumns === 2) {
      // if desktop, we check for both or last row
      return (
        index === props.items.length - 1 || index === props.items.length - 2
      );
    } else {
      return index === props.items.length - 1;
    }
  };

  return (
    <FlatList
      ref={flatListRef}
      accessibilityLabel={props.accessibilityLabel}
      numColumns={numColumns}
      key={numColumns}
      data={props.items}
      horizontal={false}
      snapToAlignment="center"
      decelerationRate="fast"
      renderItem={({ item, index }) => (
        <CarouselItem
          {...item}
          onPress={props.onItemPress}
          wrapItem={numColumns > 0}
          key={index}
          accessibilityLabel={
            props.accessibilityLabel &&
            `${props.itemAccessibilityLabel}-${index}`
          }
          /** Generate HTML and Test IDs  */
          itemId={props.id && `${props.id}-item-${index}`}
          isLastItem={isLastItem(index)}
        />
      )}
      style={props.style}
      contentContainerStyle={props.viewPortStyle}
      /** Generate HTML and Test IDs  */
      {...generateID(props.id)}
    />
  );
};

const CarouselItem = (props: CarouselItemProp & { isLastItem: boolean }) => {
  const itemRef = useRef<View>(null);
  const isBigScreen = useMediaQuery({ minWidth: 768 });
  const boxShadowConfig = '0px 2px 4px 0px rgba(0, 0, 0, 0.08)';

  useEffect(() => {
    if (Platform.OS === 'web') {
      itemRef.current?.setNativeProps({
        className: 'rnw-scroll-snap-item',
      });
    }
  }, []);

  const { id, imageUri } = props;

  return (
    <View
      style={[
        style.itemWrapper,
        isBigScreen && style.itemWrapperBigScreen,
        props.isLastItem && style.itemLastWrapper,
      ]}
      ref={itemRef}
    >
      <TouchableOpacity
        accessibilityLabel={props.accessibilityLabel}
        style={[style.item, isBigScreen && style.itemBigScreen]}
        onPress={() => props.onPress?.({ id, imageUri })}
        /** Generate HTML and Test IDs  */
        {...generateID(props.itemId)}
        /** Platform-specific props */
        {...Platform.select({
          web: {
            accessibilityRole: 'button',
          },
        })}
      >
        <Image
          source={{
            uri: props.imageUri,
          }}
          style={[
            style.itemImage,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            Platform.OS === 'web' ? { boxShadow: boxShadowConfig } : undefined,
          ]}
        />
      </TouchableOpacity>
    </View>
  );
};

const style = StyleSheet.create({
  itemWrapper: {
    marginBottom: 16,
  },
  itemWrapperBigScreen: {
    marginRight: 16,
  },
  itemLastWrapper: {
    marginBottom: 0,
  },
  item: {
    borderRadius: 24,
    width: '100%',
    aspectRatio: 344 / 342,
  },
  itemBigScreen: {
    aspectRatio: 272 / 324,
    width: '100%',
    height: 324,
  },
  itemImage: {
    resizeMode: 'cover',
    height: '100%',
    borderRadius: 24,
    shadowColor: '#00000040',
    shadowOpacity: 1,
    shadowRadius: 5,
  },
});

export default Carousel;
export { Item };
